/**
 * Funcionalidades por jornada (abas) e comparativo Antes x Depois
 */

import { useState, KeyboardEvent } from 'react';
import { Check, X } from 'lucide-react';
import { FEATURE_GROUPS, BEFORE_AFTER, BRAND } from '../content';
import { Reveal, SectionHeading } from './Reveal';

export function Features() {
  const [active, setActive] = useState(FEATURE_GROUPS[0]?.key ?? '');
  const group = FEATURE_GROUPS.find(g => g.key === active) ?? FEATURE_GROUPS[0];

  // Navegação por setas entre as abas (padrão WAI-ARIA)
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next =
      (index + (e.key === 'ArrowRight' ? 1 : -1) + FEATURE_GROUPS.length) % FEATURE_GROUPS.length;
    const target = FEATURE_GROUPS[next];
    if (target) {
      setActive(target.key);
      document.getElementById(`vw-tab-${target.key}`)?.focus();
    }
  };

  if (!group) return null;

  return (
    <section id="funcionalidades" className="relative scroll-mt-20 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Funcionalidades"
          title={
            <>
              Tudo o que o seu negócio precisa,{' '}
              <span className="vw-gradient-text">do pedido ao caixa</span>
            </>
          }
          description="Não é só um cardápio. É a operação inteira conectada: vendas, salão, pagamentos, marketing e gestão."
        />

        <Reveal className="mt-12">
          <div
            role="tablist"
            aria-label="Grupos de funcionalidades"
            className="mx-auto flex max-w-4xl gap-2 overflow-x-auto rounded-2xl border border-white/10 bg-white/[0.03] p-1.5 scrollbar-hide"
          >
            {FEATURE_GROUPS.map((g, i) => {
              const selected = g.key === group.key;
              return (
                <button
                  key={g.key}
                  id={`vw-tab-${g.key}`}
                  role="tab"
                  type="button"
                  aria-selected={selected}
                  aria-controls={`vw-panel-${g.key}`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(g.key)}
                  onKeyDown={e => onKeyDown(e, i)}
                  className={`flex min-w-fit flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-xl px-4 py-3 text-sm font-semibold transition-all ${
                    selected
                      ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-lg shadow-violet-900/40'
                      : 'text-slate-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <g.icon size={16} /> {g.label}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div
          id={`vw-panel-${group.key}`}
          role="tabpanel"
          aria-labelledby={`vw-tab-${group.key}`}
          key={group.key}
          className="mt-10 grid animate-fade-in items-start gap-8 lg:grid-cols-5"
        >
          <div className="lg:col-span-2">
            <div className="vw-gradient-border rounded-3xl bg-gradient-to-br from-violet-950/80 to-slate-900/80 p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-500/20 text-violet-200">
                <group.icon size={24} />
              </span>
              <h3 className="mt-6 text-2xl font-bold leading-snug text-white">{group.headline}</h3>
              <p className="mt-4 leading-relaxed text-slate-300">{group.description}</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-3">
            {group.items.map(item => (
              <div
                key={item.title}
                className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-all hover:-translate-y-1 hover:border-violet-400/40 hover:bg-violet-500/[0.06]"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-violet-300 transition-colors group-hover:bg-violet-500/20">
                  <item.icon size={20} />
                </span>
                <h4 className="mt-4 font-semibold text-white">{item.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function BeforeAfter() {
  return (
    <section className="relative py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Antes x Depois"
          title={
            <>
              Pare de perder pedidos.{' '}
              <span className="vw-gradient-text">Comece a ganhar tempo.</span>
            </>
          }
        />
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <Reveal className="rounded-3xl border border-rose-500/20 bg-rose-500/[0.04] p-8">
            <p className="text-sm font-semibold uppercase tracking-wider text-rose-300">
              Sem o {BRAND}
            </p>
            <ul className="mt-6 space-y-4">
              {BEFORE_AFTER.before.map(item => (
                <li key={item} className="flex gap-3 text-slate-300">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-300">
                    <X size={12} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal
            delay={120}
            className="vw-gradient-border rounded-3xl bg-gradient-to-br from-violet-950/70 to-slate-900/70 p-8"
          >
            <p className="text-sm font-semibold uppercase tracking-wider text-emerald-300">
              Com o {BRAND}
            </p>
            <ul className="mt-6 space-y-4">
              {BEFORE_AFTER.after.map(item => (
                <li key={item} className="flex gap-3 text-white">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
                    <Check size={12} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
